import { useEffect, useMemo, useState } from 'react';

import { get_testing_type } from '../utils/restapi';
import { getTestingRounds, pickCurrentRound } from '../utils/currentTesting';

/**
 * 구분(시험유형) / 시험(회차) / 일정 을 고르는 공용 선택기.
 * <ul className="form"> 안에 넣을 <li> 들만 그린다.
 *
 * onChange({ round, scheduleId }) 로 선택 결과를 알려준다.
 *   round     : { testing_id, title, type, schedules[] } 또는 null
 *   scheduleId: 선택한 일정 id, '' 이면 회차 전체
 *
 * onChange / onError 는 부모에서 useCallback 으로 고정해 넘길 것.
 */
const RoundSelector = ({ onChange, onError }) => {
  const [testingTypes, setTestingTypes] = useState([]);
  const [selectedTypeCode, setSelectedTypeCode] = useState('');
  const [rounds, setRounds] = useState([]);
  const [selectedTestingId, setSelectedTestingId] = useState('');
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [isLoadingRounds, setIsLoadingRounds] = useState(false);

  const roundObj = useMemo(
    () => rounds.find((round) => String(round.testing_id) === String(selectedTestingId)) || null,
    [rounds, selectedTestingId]
  );
  const scheduleOptions = useMemo(() => roundObj?.schedules || [], [roundObj]);

  // 구분 목록. 기본값은 '정기'
  useEffect(() => {
    let cancelled = false;

    const loadTypes = async () => {
      try {
        const types = await get_testing_type();
        if (cancelled) return;

        const typeList = types || [];
        setTestingTypes(typeList);

        const regularType = typeList.find((type) => {
          const name = `${type?.nickname || ''}${type?.name || ''}`;
          return name.includes('정기');
        });
        const defaultType = regularType || typeList[0];

        if (!defaultType?.code) {
          if (onError) onError('시험유형을 찾지 못했습니다.');
          return;
        }
        setSelectedTypeCode(String(defaultType.code));
      } catch (error) {
        console.error('Error fetching testing types:', error);
        if (!cancelled && onError) onError(`시험유형을 불러오지 못했습니다. (${error?.message || '네트워크 오류'})`);
      }
    };

    loadTypes();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 선택한 구분의 회차 목록(최신 먼저). 기본 선택은 이번 회차
  useEffect(() => {
    let cancelled = false;

    const loadRounds = async () => {
      if (!selectedTypeCode) return;

      setIsLoadingRounds(true);
      try {
        const typeRounds = await getTestingRounds(selectedTypeCode);
        if (cancelled) return;

        setRounds(typeRounds);
        setSelectedScheduleId('');

        if (!typeRounds.length) {
          setSelectedTestingId('');
          if (onError) onError('선택한 구분에 등록된 시험 회차가 없습니다.');
          return;
        }

        const current = pickCurrentRound(typeRounds);
        setSelectedTestingId(current ? String(current.testing_id) : '');
        if (onError) onError('');
      } catch (error) {
        console.error('Error fetching rounds:', error);
        if (!cancelled) {
          setRounds([]);
          setSelectedTestingId('');
          if (onError) onError(`회차 정보를 불러오지 못했습니다. (${error?.message || '네트워크 오류'})`);
        }
      } finally {
        if (!cancelled) setIsLoadingRounds(false);
      }
    };

    loadRounds();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypeCode]);

  // 선택 결과 전달
  useEffect(() => {
    if (onChange) onChange({ round: roundObj, scheduleId: selectedScheduleId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundObj, selectedScheduleId]);

  const handleTypeChange = (e) => setSelectedTypeCode(e.target.value);

  const handleRoundChange = (e) => {
    setSelectedTestingId(e.target.value);
    setSelectedScheduleId('');
  };

  const handleScheduleChange = (e) => setSelectedScheduleId(e.target.value);

  return (
    <>
      <li>
        <label>구분</label>
        <span>
          <select value={selectedTypeCode} onChange={handleTypeChange}>
            <option value="">▒ 선택 ▒</option>
            {testingTypes.map((obj, index) => (
              <option key={index} value={obj.code}>
                {obj.nickname || obj.name}
              </option>
            ))}
          </select>
        </span>
      </li>
      <li>
        <label>시험</label>
        <span>
          <select value={selectedTestingId} onChange={handleRoundChange} disabled={!rounds.length}>
            <option value="">{isLoadingRounds ? '▒ 불러오는 중 ▒' : '▒ 선택 ▒'}</option>
            {rounds.map((round, index) => (
              <option key={index} value={round.testing_id}>
                {round.title}
              </option>
            ))}
          </select>
        </span>
      </li>
      <li>
        <label>일정</label>
        <span>
          <select value={selectedScheduleId} onChange={handleScheduleChange} disabled={!scheduleOptions.length}>
            <option value="">▒ 전체 일정 ▒</option>
            {scheduleOptions.map((obj, index) => (
              <option key={index} value={obj.testing_schedule_id}>
                {obj.testStartDate} ~ {obj.testStopDate}
              </option>
            ))}
          </select>
        </span>
      </li>
    </>
  );
};

export default RoundSelector;
